import { NodeFormValues } from "@features/builder/NodeDrawer/NodeForm"
import PropertyField from "@features/builder/NodeDrawer/NodeForm/PropertyField"
import { isValidUnitFormat } from "@utils/format/nodeUnitFormat"
import UnitInfoButton from "@features/builder/NodeDrawer/NodeForm/UnitFields/UnitInfoButton"
import { Info } from "@mui/icons-material"
import { Alert, FormControlLabel, Grid, MenuItem, Switch, Tooltip } from "@mui/material"
import { Controller, useFormContext } from "react-hook-form"
import UnitToggleOffWarningDialog from "@features/builder/NodeDrawer/NodeForm/UnitFields/UnitToggleOffWarningDialog"
import { useState } from "react"

export const DECIMAL_PLACES_OPTIONS = ["0", "1", "2", "3", "4", "5"]

type UnitFieldProps = {
  originalNodeUnitFormat: string
}

const UnitField = (props: UnitFieldProps) => {
  const form = useFormContext<NodeFormValues>()
  const isUnitEmpty = form.watch("unit.isEmpty")

  const originalNodeUnitFormat = props.originalNodeUnitFormat
  const isOriginalUnitValid = isValidUnitFormat(originalNodeUnitFormat)

  const [isUnitToggleOffWarningDialogOpen, setIsUnitToggleOffWarningDialogOpen] = useState(false)

  const handleUnitToggleOffWarningDialogClose = () => {
    setIsUnitToggleOffWarningDialogOpen(false)
  }

  const handleUnitToggleOffWarningDialogConfirm = () => {
    setEmptyUnitFlag(true)
    handleUnitToggleOffWarningDialogClose()
  }

  const setEmptyUnitFlag = (isEmpty: boolean) => {
    form.setValue("unit.isEmpty", isEmpty, {
      shouldDirty: true,
    })
  }

  const handleUnitToggleChange = (checked: boolean) => {
    if (!checked) {
      setIsUnitToggleOffWarningDialogOpen(true)
      return
    }

    setEmptyUnitFlag(!checked)
  }

  return (
    <>
      <Grid container direction="column" gap={1}>
        <Grid container direction="row" justifyContent="space-between">
          <Grid item>
            <Controller
              control={form.control}
              name="unit.isEmpty"
              render={({ field }) => (
                <FormControlLabel
                  label="Unit format"
                  sx={{ ml: 0 }}
                  control={
                    <Switch
                      {...field}
                      checked={!field.value}
                      onChange={(event) => handleUnitToggleChange(event.target.checked)}
                    />
                  }
                />
              )}
            />
          </Grid>
          <Grid item>
            <UnitInfoButton />
          </Grid>
        </Grid>

        {/* show non-blocking warning if user's node is using outdated (unsupported) unit format */}
        {!isOriginalUnitValid && isUnitEmpty && (
          <Grid item>
            <Alert severity="warning">
              "{originalNodeUnitFormat}" unit format is invalid. Toggle on switch above to update it
              to valid one.
            </Alert>
          </Grid>
        )}

        {!isUnitEmpty && (
          <Grid item>
            <fieldset style={{ color: "rgba(0, 0, 0, 0.6)", borderColor: "rgba(0, 0, 0, 0.6)" }}>
              <legend>Unit settings</legend>
              <Grid container direction="column" gap={1}>
                <Grid item>
                  <Controller
                    control={form.control}
                    name="unit.usePercentageValue"
                    render={({ field }) => (
                      <FormControlLabel
                        label="Show as percentage"
                        sx={{ ml: 0 }}
                        control={
                          <Switch
                            {...field}
                            checked={field.value}
                            onChange={(event) => field.onChange(event.target.checked)}
                          />
                        }
                      />
                    )}
                  />
                  <Tooltip title="Show values as percentages. For example, 0.35 will be shown as 35%">
                    <Info sx={{ verticalAlign: "middle" }} />
                  </Tooltip>
                </Grid>

                <Grid item>
                  <PropertyField
                    control={form.control}
                    name="unit.decimalPlaces"
                    label="Decimal Places"
                    select
                  >
                    {DECIMAL_PLACES_OPTIONS.map((value) => (
                      <MenuItem key={value} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </PropertyField>
                </Grid>

                <Grid item>
                  <PropertyField control={form.control} label="Suffix" name="unit.suffix" />
                </Grid>
              </Grid>
            </fieldset>
          </Grid>
        )}
      </Grid>
      <UnitToggleOffWarningDialog
        open={isUnitToggleOffWarningDialogOpen}
        onConfirm={handleUnitToggleOffWarningDialogConfirm}
        onCancel={handleUnitToggleOffWarningDialogClose}
      />
    </>
  )
}

UnitField.displayName = "UnitField"
export default UnitField
